import ENS_PUBLIC_RESOLVER_ABI from 'abis/ens-public-resolver.json'
import ENS_ABI from 'abis/ens-registrar.json'
import { WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { ENS_REGISTRAR_ADDRESSES } from 'constants/addresses'
import useDebounce from 'hooks/useDebounce'
import { useMemo } from 'react'
import type { Abi, Address } from 'viem'
import { namehash } from 'viem/ens' // <-- NEW: Import namehash from viem/ens
import { useReadContract } from 'wagmi'

import isZero from '../utils/isZero'

/**
 * Does a lookup for an ENS name to find its address.
 */
export default function useENSAddress(ensName?: string | null): { loading: boolean; address: string | null } {
  const debouncedName = useDebounce(ensName, 200)
  const ensNodeArgument = useMemo(() => {
    if (!debouncedName) return undefined
    try {
      return namehash(debouncedName)
    } catch {
      return undefined // Return undefined if namehash fails (e.g., empty string)
    }
  }, [debouncedName])

  // Step 1: Get resolver address from ENS registrar
  const { data: resolverAddressResult, isLoading: resolverLoading } = useReadContract({
    address: ENS_REGISTRAR_ADDRESSES[1], // Mainnet only
    abi: ENS_ABI as Abi,
    functionName: 'resolver',
    args: ensNodeArgument ? [ensNodeArgument as Address] : undefined,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!ensNodeArgument,
    },
  })

  const resolverAddress = useMemo(() => {
    if (!resolverAddressResult || isZero(resolverAddressResult as string)) return undefined
    return resolverAddressResult as Address
  }, [resolverAddressResult])

  // Step 2: Get address from resolver
  const { data: addrResult, isLoading: addrLoading } = useReadContract({
    address: resolverAddress,
    abi: ENS_PUBLIC_RESOLVER_ABI as Abi,
    functionName: 'addr',
    args: ensNodeArgument ? [ensNodeArgument as Address] : undefined,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!resolverAddress && !!ensNodeArgument,
    },
  })

  const changed = debouncedName !== ensName
  return useMemo(
    () => ({
      address: changed ? null : (addrResult as string) ?? null,
      loading: changed || resolverLoading || addrLoading,
    }),
    [addrLoading, addrResult, changed, resolverLoading]
  )
}
